import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import Logger from '../api/controllers/logger'

export default class MAOService {
    #lineItemContainer = null
    #authorization = ''
    #inventoryReservationPayload = null
    #inventoryReservationSucceeded = undefined

    constructor(lineItemContainer, authorization) {
        this.#lineItemContainer = lineItemContainer
        this.#authorization = authorization
    }

    get #CONFIG_NAME_MAO() {
        return 'mao'
    }

    get #CONFIG_NAME_CHANGEUP() {
        return 'changeUp'
    }

    get #MRT_INVENTORY_DETAILS_ENDPOINT() {
        return '/api/mao/inventory/details'
    }

    get #MRT_INVENTORY_RESERVATION_ENDPOINT() {
        return '/api/mao/inventory/reserve'
    }

    get inventoryReservationSucceeded() {
        return this.#inventoryReservationSucceeded
    }

    get inventoryReservationPayload() {
        return this.#inventoryReservationPayload
    }

    get #productIDs() {
        return this.#lineItemContainer?.productItems
            ?.map(({productId}) => productId)
            ?.filter((id) => id !== 'colorbar')
            ?.map((id) => id?.substring(1, id.length - 1))
    }

    async #callMRTController(path, body) {
        const requestURL = onClient() ? path : new URL(`${getAppOrigin()}${path}`)
        return await fetch(requestURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: this.#authorization
            },
            body: JSON.stringify(body)
        })
    }

    #getConfigFor(configName) {
        const {
            app: {commerceAPI}
        } = getConfig()
        const config = commerceAPI[configName]?.config || commerceAPI[configName]?.configChangeup

        if (!config.length) {
            console.error(`No ${configName} Configs are present`)
            return
        }

        try {
            return JSON.parse(config)
        } catch (e) {
            console.error(`${configName} Configs not configured correctly`)
            return
        }
    }

    async #getAvailabilityBody(store) {
        let maoViewDefinitionID = ''
        let maoViewName = ''
        let maoViewConfigID = ''
        let maoViewconfigName = ''

        try {
            const maoConfig = this.#getConfigFor(this.#CONFIG_NAME_MAO)
            const {viewDefinitionID, viewName, viewConfigID, viewConfigName} = maoConfig || {}

            maoViewDefinitionID = viewDefinitionID
            maoViewName = viewName
            maoViewConfigID = viewConfigID
            maoViewconfigName = viewConfigName
        } catch (e) {
            console.error('MAO Configs not configured correctly')
        }

        return {
            Items: this.#productIDs,
            Locations: [store.ID],
            ViewDefinitionId: maoViewDefinitionID,
            ViewName: maoViewName,
            viewConfigId: maoViewConfigID,
            viewConfigName: maoViewconfigName
        }
    }

    async #getInventoryReservationBody(order) {
        const shipmentId = order?.shipments[0]?.shippingMethod?.id
        const storeId = order?.shipments[0]?.c_fromStoreId
        const changeUpConfig = this.#getConfigFor(this.#CONFIG_NAME_CHANGEUP) || {}
        const changeUpProductID = changeUpConfig.enabled ? changeUpConfig.productID : null
        const requestDetail = []

        order?.productItems?.forEach((pli, index) => {
            if (pli.productId !== changeUpProductID) {
                requestDetail.push({
                    DeliveryMethodId: shipmentId,
                    ItemId: pli.c_sku || pli.productId,
                    PromisingRequestDetailId: (index + 1).toString(), // start at "1"
                    Quantity: pli.quantity,
                    ShipFromLocationId: storeId || pli.c_fromStoreId // both values should be the same
                })
            }
        })

        return {
            PromisingRequestId: order.orderNo || order?.c_orderNo,
            DemandType: 'Allocation',
            RequestType: 'Reservation',
            PromisingRequestDetail: requestDetail
        }
    }

    async #fetchAvailability(store) {
        const payload = await this.#getAvailabilityBody(store)
        const clientResponse = await this.#callMRTController(
            this.#MRT_INVENTORY_DETAILS_ENDPOINT,
            payload
        )

        if (clientResponse.status >= 300) {
            throw new Error(clientResponse)
        }

        return await clientResponse.json()
    }

    async #reserveInventory() {
        this.#inventoryReservationPayload = await this.#getInventoryReservationBody(
            this.#lineItemContainer
        )
        Logger.warn('MAO Payload', JSON.stringify(this.#inventoryReservationPayload))
        const clientResponse = await this.#callMRTController(
            this.#MRT_INVENTORY_RESERVATION_ENDPOINT,
            this.#inventoryReservationPayload
        )
        const JsonResponse = await clientResponse.json()
        Logger.warn('MAO Response', JSON.stringify(JsonResponse))
        if (clientResponse.status >= 300) {
            throw new Error(clientResponse)
        }

        return JsonResponse
    }

    shouldReserveInventory() {
        // only reserve inventory for BOPIS orders
        return this.#lineItemContainer?.shipments?.some((shipment) => {
            return shipment.shippingMethod?.c_storePickupEnabled === true
        })
    }

    async handleInventoryReservation(order) {
        try {
            const inventoryReservationResponse = await this.#reserveInventory()
            const reserveInventoryHash = new Map()

            if (!inventoryReservationResponse?.data?.PromisingRequestDetailList?.length) {
                Logger.error('Reservation data from MAO is not available. ', order?.orderNo)
                throw new Error('Reservation data from MAO is not available.')
            }
            Logger.info(
                'This is inventoryReservationResponse',
                JSON.stringify(inventoryReservationResponse)
            )
            inventoryReservationResponse?.data?.PromisingRequestDetailList.forEach(
                (reservation) => {
                    const itemId = reservation?.ItemId
                    const quantity = reservation?.Allocation.reduce(function (
                        totalQty,
                        allocationItem
                    ) {
                        return totalQty + allocationItem.Quantity
                    },
                    0)

                    reserveInventoryHash.set(itemId, quantity)
                }
            )

            this.#lineItemContainer?.productItems.some((pli) => {
                var manufacturerSKU = pli.c_sku || pli.productId
                var maoReservedInventory = manufacturerSKU
                    ? reserveInventoryHash.get(manufacturerSKU)
                    : 0

                if (maoReservedInventory < pli.quantity) {
                    Logger.error(
                        `Not enough inventory ${maoReservedInventory} reserved for items in the order ${order?.orderNo}`
                    )
                    throw new Error('Not enough inventory reserved for items in the order.')
                }
            })

            this.#inventoryReservationSucceeded = true
        } catch (error) {
            this.#inventoryReservationSucceeded = false
            Logger.error('**********')
            Logger.error(`Inventory reservation failed for order: ${order?.orderNo}`)
            Logger.error('This is error.message', error.message)
            Logger.error('This is error.cause', error.cause)
            Logger.error('This is error.message (stringify)', JSON.stringify(error.message))
            Logger.error('This is error.cause (stringify)', JSON.stringify(error.cause))
            Logger.error('Error log for MAO inventory issue: ', JSON.stringify(error))
            Logger.error('**********')
            throw new Error(error)
        }
    }

    async getProductAvailabilityForStore(store) {
        let result = []
        try {
            const invantoryDetailsResponse = await this.#fetchAvailability(store)
            result = invantoryDetailsResponse.data.map((item) => {
                return {
                    productId: item.ItemId,
                    quantity: item.Quantity
                }
            })
        } catch (error) {
            console.error('Error fetching product availability for store:', error)
        }

        return result
    }
}
